import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InfoComponent } from "@components/info/info.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { LabelComponent } from "@components/label/label.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { NgMultiLabelTemplateDirective, NgSelectComponent } from "@ng-select/ng-select";
import { MatningsinstruktionDTO } from "~app/common/models/produkt/MatningsinstruktionDTO";
import { ProduktDTO } from "~app/common/models/produkt/ProduktDTO";
import { ProduktLangdmodulDTO } from "~app/common/models/produkt/ProduktLangdmodulDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { FormatService } from "~app/common/services/format.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ProduktDetailTexts } from "../../produkt-detail.texts";
import { HandelssortimentsgruppKategori } from "../../produkt-details.models";
import { ProduktDetailBasValidation } from "./produkt-detail-bas.validation";

@Component({
    selector: "rask-produkt-detail-bas",
    templateUrl: "./produkt-detail-bas.component.html",
    styleUrls: ["./produkt-detail-bas.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        BlockComponent,
        TextareaComponent,
        InputComponent,
        ButtonComponent,
        InputNumberComponent,
        CheckboxComponent,
        InfoComponent,
        LabelComponent,
        NgSelectComponent,
        NgMultiLabelTemplateDirective
    ]
})
export class ProduktDetailBasComponent implements OnInit {
    form: UntypedFormGroup;
    isEditableRole: boolean;
    manuelltKap: boolean;
    newMatningsinstruktionlank: string;
    newMatningsintruktiontext: string;
    nyLangdmodul: number;
    @Input() produkt: ProduktDTO;
    produktLangdInterval = 30;
    @Input() produktspecifikation: ProduktspecifikationDTO;
    @Input() rootForm: UntypedFormGroup;
    texts = ProduktDetailTexts.basTab.texts;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly fieldsToUseInForm = [
        "sarskildInfo",
        "diaToppMin",
        "diaToppMax",
        "diaRotMax",
        "langdgransMin",
        "langdgransMax",
        "produktLangdmoduler",
        "overmalMin",
        "overmalMax",
        "fukthalt",
        "flisstorlekOnskad"
    ];
    private readonly formBinder = inject(FormBinderService);
    private readonly formatService = inject(FormatService);

    get addLangdmodulDisabled() {
        return !this.isEditableRole || !this.nyLangdmodul || this.validationContext.errors.some(p => p === ProduktDetailTexts.basTab.validation.nyLangdmodul);
    }

    get addMatningsinstruktionDisabled() {
        return !this.isEditableRole ||
            this.newMatningsintruktiontext.length <= 0 ||
            this.newMatningsinstruktionlank.length <= 0 ||
            this.validationContext.errors.some(p => p === ProduktDetailTexts.basTab.validation.matningsinstruktionslank);
    }

    get isEnergi(): boolean {
        return this.produkt?.handelssortimentsgrupp?.kategori === HandelssortimentsgruppKategori.energi;
    }

    get isEnergiOrOvrigt(): boolean {
        return this.produkt?.handelssortimentsgrupp?.kategori === HandelssortimentsgruppKategori.energi ||
            this.produkt?.handelssortimentsgrupp?.kategori === HandelssortimentsgruppKategori.ovrigt;
    }

    get skapaLangdmodulerDisabled(): boolean {
        return this.validationContext.errors.some(p => p === ProduktDetailTexts.basTab.validation.langdgransMin ||
                    p === ProduktDetailTexts.basTab.validation.langdgransMax) ||
               !this.produktspecifikation.langdgransMin ||
               !this.produktspecifikation.langdgransMax ||
               !this.produktLangdInterval ||
               this.produktLangdInterval <= 0 ||
               !this.isEditableRole;
    }

    readonly addMatningsinstruktion = (): void => {
        if (this.addMatningsinstruktionDisabled) {
            return;
        }

        this.produktspecifikation.matningsinstruktioner.push({
            id: null,
            beskrivning: this.newMatningsintruktiontext,
            url: this.newMatningsinstruktionlank,
            iBy: this.authenticationService.getUserData().fullUserName,
            iDate: new Date().toString()
        });

        this.newMatningsinstruktionlank = "";
        this.newMatningsintruktiontext = "";
        this.form.controls.matningsinstruktioner.markAsDirty();
    };

    readonly deleteMatningsinstruktion = (id: number) => {
        const index = this.produktspecifikation.matningsinstruktioner.findIndex(p => p.id === id);
        this.produktspecifikation.matningsinstruktioner.splice(index, 1);
        this.form.controls.matningsinstruktioner.markAsDirty();
    };

    getMatningsinstruktionLadesTill(matningsinstruktion: MatningsinstruktionDTO): string {
        return `${this.formatService.dateFormatRask(matningsinstruktion.iDate, false)} av ${matningsinstruktion.iBy}`;
    }

    readonly laggTillLangdmodul = (): void => {
        if (this.addLangdmodulDisabled) {
            return;
        }

        this.produktspecifikation.produktLangdmoduler.push({
            id: null,
            modul: this.nyLangdmodul,
            endastManuellaKap: this.manuelltKap
        });

        this.patchLangdmoduler(this.produktspecifikation.produktLangdmoduler);
        this.nyLangdmodul = null;
    };

    langdmodulChangeEvent($event: any) {
        this.produktspecifikation.produktLangdmoduler = $event;
    }

    readonly langdmodulCheckboxClicked = (checked: boolean, item: ProduktLangdmodulDTO) => {
        item.endastManuellaKap = checked;
        this.patchLangdmoduler(this.produktspecifikation.produktLangdmoduler);
    };

    ngOnInit(): void {
        this.isEditableRole = this.authenticationService.canEdit();
        this.newMatningsinstruktionlank = "";
        this.newMatningsintruktiontext = "";
        this.createForm();
    }

    readonly skapaLangdmoduler = (): void => {
        if (this.skapaLangdmodulerDisabled) {
            return;
        }

        let langdmoduler: ProduktLangdmodulDTO[] = this.produktspecifikation.produktLangdmoduler;
        if (!langdmoduler) {
            langdmoduler = [];
        }

        for (let i = this.produktspecifikation.langdgransMin; i <= this.produktspecifikation.langdgransMax; i += this.produktLangdInterval) {
            if (langdmoduler && !langdmoduler.some(l => l.modul === i)) {
                langdmoduler.push({
                    modul: i,
                    endastManuellaKap: false,
                    id: null
                });
            }
        }

        this.patchLangdmoduler(langdmoduler);
    };

    private createForm(): void {
        this.form = this.formBinder.CreateGroup(this.produktspecifikation, this.fieldsToUseInForm);
        this.form.valueChanges.subscribe(this.onFormChanges);

        this.validationContext = ProduktDetailBasValidation.init(this.produktspecifikation, this.newMatningsinstruktionlank, this.nyLangdmodul);
        this.validationContext
            .scope(ValidationScopes.spara)
            .subscribe(v => { this.validation = v; });
        this.formBinder.attachValidation(this.validationContext.current, this.form);

        this.rootForm.addControl(`produkt-detail-bas`, this.form);
        this.form.markAsPristine();
        this.validationContext.validate();
    }

    private readonly onFormChanges = () => {
        this.validationContext.validate();
    };

    private readonly patchLangdmoduler = (langdmoduler: ProduktLangdmodulDTO[]) => {
        this.form.patchValue({
            produktLangdmoduler: langdmoduler
        });
        this.form.controls.produktLangdmoduler.markAsDirty();
    };
}
